// ============================================================================
// Screenshot cache — persists captured screenshots to IndexedDB
// ============================================================================

const DB_NAME = 'aplus-screenshots'
const DB_VERSION = 1
const STORE_NAME = 'captures'

export interface CachedScreenshot {
  /** Composite key: "ASIN::MARKETPLACE_CODE" */
  key: string
  asin: string
  marketplace: string
  /** Base64 data URL (data:image/jpeg;base64,...) */
  dataUrl: string
  /** ISO date string when screenshot was captured */
  capturedAt: string
}

function makeKey(asin: string, marketplace: string): string {
  return `${asin}::${marketplace}`
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/** Save a screenshot blob to the cache */
export async function saveScreenshot(
  asin: string,
  marketplace: string,
  blob: Blob,
): Promise<CachedScreenshot> {
  const dataUrl = await blobToDataUrl(blob)
  const entry: CachedScreenshot = {
    key: makeKey(asin, marketplace),
    asin,
    marketplace,
    dataUrl,
    capturedAt: new Date().toISOString(),
  }

  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put(entry)
    tx.oncomplete = () => resolve(entry)
    tx.onerror = () => reject(tx.error)
  })
}

/** Load a cached screenshot (returns null if not cached) */
export async function loadScreenshot(
  asin: string,
  marketplace: string,
): Promise<CachedScreenshot | null> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).get(makeKey(asin, marketplace))
    req.onsuccess = () => resolve(req.result || null)
    req.onerror = () => reject(req.error)
  })
}

/** Load all cached screenshots for a given ASIN */
export async function loadAllScreenshots(
  asin: string,
): Promise<CachedScreenshot[]> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).getAll()
    req.onsuccess = () => {
      const all: CachedScreenshot[] = req.result || []
      resolve(all.filter((entry) => entry.asin === asin))
    }
    req.onerror = () => reject(req.error)
  })
}

/** Format a capturedAt ISO string for display */
export function formatCapturedAt(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  // Show date + time for older captures
  const month = d.toLocaleString('en-US', { month: 'short' })
  const day = d.getDate()
  const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  const year = d.getFullYear() !== now.getFullYear() ? `, ${d.getFullYear()}` : ''
  return `${month} ${day}${year} at ${time}`
}

// Helper to convert Blob → data URL
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}
